<script setup lang="ts">
  /**
   * Reset Password View
   *
   * Set new password using reset token. Serves BOTH apps from packages/shared —
   * a carrier arriving from a portal invite and a staff member resetting on ops
   * — so the framing is domain-aware (`getAppDomain`) while the form is one
   * implementation. Anatomy deliberately mirrors LoginView: an invitee sets a
   * password and immediately signs in, and those two screens should read as one
   * place, not two products.
   */
  import { computed, ref, onMounted } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { authService } from '../../services/auth.service'
  import { classifyResetError, policyReasonFrom } from './reset-password-errors'
  import { isPastExpiry } from './reset-token-expiry'
  import { useNotifications } from '../../composables/useNotifications'
  import { useAuthStore } from '../../stores/auth'
  import { getAppDomain, type AppDomain } from '../../utils/domain'
  import { evaluatePassword } from '../../utils/passwordPolicy'
  import logoHorizontal from '../../assets/logos/claimshield-horizontal.png'
  import logoHorizontalReverse from '../../assets/logos/claimshield-horizontal-reverse.png'

  const router = useRouter()
  const route = useRoute()
  const authStore = useAuthStore()
  const { showSuccess, showError } = useNotifications()

  /**
   * Which framing to show — and which way to fail.
   *
   * `getAppDomain()` returns 'marketing' for any host that is not `portal.*` or
   * `ops.*`, localhost included. So the fallback is not a dev detail: it decides
   * what an unrecognised host shows a real person. Ops copy is the wrong default
   * — a staff member seeing client-portal framing costs nothing, a carrier
   * reading "Internal Ops / Where compliance gets done" is an internal voice
   * leaking to a customer. Ops is therefore opt-in by hostname; everything else
   * gets the client framing.
   */
  const currentDomain: AppDomain = getAppDomain()
  const isOps = computed(() => currentDomain === 'ops')
  /**
   * Staff-only overline — see LoginView for the reasoning. "Client Portal" is
   * our taxonomy for our three portals; it orients us and nobody else. On this
   * screen the orienting work is done properly by the account line below the
   * heading ("You'll use it to sign in as …"), which is about the reader
   * rather than about our org chart.
   */
  const domainLabel = computed(() => 'Operations')

  /**
   * The account being set up, for display only.
   *
   * The reset token carries a user id and nothing human-readable, so the screen
   * could not say what it was a password FOR — the second half of the 2026-08-13
   * finding. When the invite link passes `?email=`, show it; when it doesn't,
   * fall back to copy that stands on its own. Never trusted for anything: the
   * token alone decides whose password changes.
   */
  const accountEmail = computed(() => {
    const value = route.query.email
    return typeof value === 'string' && value.includes('@') ? value : ''
  })

  /**
   * Which moment this is — a welcome, or a recovery.
   *
   * This one screen serves both a first-time portal invite and an ordinary
   * forgot-password reset, and the token cannot tell them apart: it carries a
   * user id and a password fingerprint, nothing else. So the link says, via
   * `?flow=` (minted by `portal_invite.set_password_url`). Display only — it
   * selects wording and nothing else, so a tampered value changes a heading and
   * no authority whatsoever.
   *
   * **Absent means invite, not unknown.** Invite tokens live 7 days, so
   * unmarked links minted before `flow` existed keep arriving for a week after
   * this ships; they must read exactly as they did when they were sent, and
   * what shipped then is the invite wording below.
   */
  const isReset = computed(() => route.query.flow === 'reset')

  /**
   * Headings borrowed verbatim from each email's own CTA button
   * (`portal_invite.render_invite_email` → "Set your password";
   * `render_reset_email` → "Choose a new password"), so the screen finishes the
   * sentence the email started instead of greeting the reader with a new one.
   */
  const heading = computed(() =>
    isReset.value ? 'Choose a new password' : 'Set your password',
  )

  /**
   * The subline for links that carry no `?email=`. When one does, the template
   * renders the sentence itself so the address can stay visually distinct —
   * see the comment there.
   */
  const subheadingWithoutAccount = computed(() =>
    isReset.value
      ? 'This replaces your current password.'
      : "You'll use it to sign in from now on.",
  )

  /**
   * "Set" is the first-time verb, "save" the recovery one — the same
   * distinction the headings make, carried through to the control so the screen
   * does not change its mind halfway down.
   *
   * "and continue" is a promise, and it is only made when it can be kept: the
   * success path signs the reader straight in, which needs the account address
   * the link carries. A link without one ends on the sign-in page, so its
   * button stops short of promising more (P5, 2026-09-22 — the button said
   * "and continue" and dropped every invitee at /login).
   */
  const submitLabel = computed(() => {
    const verb = isReset.value ? 'Save password' : 'Set password'
    return accountEmail.value ? `${verb} and continue` : verb
  })

  /**
   * The tab title the route cannot know. The router's `meta.title` is fixed per
   * path, and this one path serves a welcome and a recovery, so it said "Reset
   * Password" above a page that says "Set your password" (P6). Only the first
   * half is replaced; the app suffix ("ClaimShield Portal") is the route's.
   */
  function applyInviteTitle() {
    if (isReset.value) return
    const routeTitle = typeof route.meta?.title === 'string' ? route.meta.title : ''
    const suffix = routeTitle.split(' | ').slice(1).join(' | ')
    document.title = suffix ? `Set Your Password | ${suffix}` : 'Set Your Password'
  }

  /**
   * The expired state can finally name a duration.
   *
   * It deliberately did not, because the two link types do not share one
   * (INVITE_TOKEN_LIFETIME_SECONDS = 7 days, RESET_TOKEN_LIFETIME_SECONDS =
   * 1 hour) and the screen could not tell which had expired — so naming either
   * was wrong half the time. `flow` removes that constraint. The durations are
   * mirrored from those two server constants; if either moves, this moves.
   *
   * The invite line offers the consultant as well as the self-serve link
   * because the invite email itself says to ask them — the screen should not
   * contradict the email that brought the reader here. Both routes work.
   */
  const expiredHeading = computed(() =>
    isReset.value ? 'This link has expired' : 'This invite has expired',
  )

  const expiredSubheading = computed(() =>
    isReset.value
      ? "Reset links last 1 hour. Request a new one and we'll email it to you."
      : 'Invite links last 7 days. Request a new one below, or ask your ClaimShield consultant.',
  )

  const password = ref('')
  const confirmPassword = ref('')
  const loading = ref(false)
  const token = ref('')
  const tokenValid = ref(true)

  // Mirror of the server policy (app/auth/password_policy.py) so the rules are
  // visible while typing. The server is the control — a green checklist here is
  // a courtesy, never a guarantee, which is why a server rejection below is
  // shown verbatim rather than translated.
  // Feed the checklist the same account context the server uses, when the link
  // carries it. Without this the context rule shows green for a password built
  // from the carrier's own domain and the server refuses it anyway — a mirror
  // that disagrees with the control is worse than no mirror.
  const policyRules = computed(() =>
    evaluatePassword(password.value, { email: accountEmail.value || undefined }),
  )
  const policyMet = computed(() => policyRules.value.every(rule => rule.met))
  const passwordsMatch = computed(
    () => confirmPassword.value.length > 0 && password.value === confirmPassword.value,
  )
  const canSubmit = computed(() => policyMet.value && passwordsMatch.value && !loading.value)

  onMounted(() => {
    applyInviteTitle()
    // Get token from URL query params
    token.value = (route.query.token as string) || ''
    // A link whose own expiry has passed is dead on arrival — say so now, not
    // after the reader has built a password for it (P1). See reset-token-expiry
    // for why this can only ever err toward showing the form.
    if (!token.value || isPastExpiry(token.value)) {
      tokenValid.value = false
    }
  })

  /**
   * Pull the policy failure out of a 400.
   *
   * The reset endpoint answers a bad/expired token with a bare string detail
   * and a policy rejection with `{code, reason}`. Treating both as "expired
   * link" (as this view used to) sent a user with a perfectly good token off
   * to request another one.
   */

  async function handleSubmit() {
    if (!password.value || !confirmPassword.value) {
      showError('Error', 'Please fill in all fields')
      return
    }

    if (password.value !== confirmPassword.value) {
      showError('Error', 'Passwords do not match')
      return
    }

    if (!policyMet.value) {
      const unmet = policyRules.value.filter(rule => !rule.met).map(rule => rule.label)
      showError('Password too weak', unmet.join(' · '))
      return
    }

    loading.value = true

    try {
      await authService.resetPassword({
        token: token.value,
        password: password.value,
      })
      // Drop whatever session this browser was already carrying BEFORE
      // redirecting. Without this, an invitee who happens to be logged in as
      // someone else lands on /login, the router guard sees them as
      // authenticated and forwards them to *that* account's dashboard — every
      // on-screen signal says the new login worked while they are looking at a
      // different user's data. Observed 2026-08-13: a portal dashboard was on
      // screen while the invited account's `last_login_at` was still NULL.
      // (The backend already revokes the *reset* user's refresh tokens in
      // `on_after_reset_password`; this is the other side of the same coin.)
      await authStore.logout()

      // Keep the button's promise (P5). The reader proved who they are with the
      // emailed token four seconds ago; asking them to type the password again
      // on /login was a second proof of the same thing. The address comes from
      // the link and is display-only everywhere else on this screen — here it
      // is only a username to try, and the server decides: a wrong or tampered
      // address simply fails to sign in and falls through to the sign-in page,
      // with the password already set either way.
      const setVerb = isReset.value ? 'Password updated' : 'Password set'
      if (accountEmail.value) {
        try {
          await authStore.login({ username: accountEmail.value, password: password.value })
          showSuccess(setVerb, "You're signed in.")
          router.push('/')
          return
        } catch {
          // Fall through: the password change itself succeeded.
        }
      }
      showSuccess(setVerb, 'Sign in with your new password to continue.')
      router.push('/login')
    } catch (error) {
      switch (classifyResetError(error)) {
        case 'policy':
          // The token is fine — only the password was refused. Keep the form up.
          showError('Choose a different password', policyReasonFrom(error) as string)
          break
        case 'bad-token':
          showError('Error', 'Failed to reset password. The link may have expired.')
          tokenValid.value = false
          break
        default:
          // No verdict from the server, so the link is NOT known to be dead.
          showError('Please try again', "We couldn't reach our service right now. Please try again.")
      }
    } finally {
      loading.value = false
    }
  }
</script>

<template>
  <div class="auth-page flex min-h-screen bg-white">
    <div class="flex w-full">

      <!-- Left panel — the Breathing Surface, same recipe and inset as LoginView -->
      <div class="mesh-panel relative m-3 hidden w-7/12 rounded-3xl lg:flex lg:flex-col lg:justify-between lg:px-10 lg:pb-10 lg:pt-8">
        <div class="mesh-bg rounded-3xl" />

        <div class="relative z-10 pt-2">
          <img
            :src="logoHorizontalReverse"
            alt="ClaimShield"
            class="h-9 w-auto"
          />
        </div>

        <div class="relative z-10">
          <template v-if="isOps">
            <p class="text-xs font-semibold uppercase tracking-[0.08em] text-white/85">Internal Ops</p>
            <p class="mt-2 text-2xl font-semibold leading-tight text-white">
              Where compliance<br />gets done.
            </p>
          </template>
          <template v-else>
            <p class="text-2xl font-semibold leading-tight text-white">
              Your fleet. Your data.<br />Always current.
            </p>
          </template>
        </div>
      </div>

      <!-- Right panel — form column, capped at the canon's 440px focus width -->
      <div class="flex w-full flex-col justify-center px-8 py-10 lg:w-5/12 lg:px-14 lg:py-14">
       <div class="mx-auto w-full max-w-[440px]">

        <!-- Mobile logo — the mesh panel is hidden below lg, so the mark lives here -->
        <div class="mb-6 lg:hidden">
          <img
            :src="logoHorizontal"
            alt="ClaimShield"
            class="h-8 w-auto"
          />
        </div>

        <!-- Invalid token state -->
        <template v-if="!tokenValid">
          <div class="mb-6">
            <span
              v-if="isOps"
              class="text-xs font-semibold uppercase tracking-[0.08em] text-primary-700"
            >
              {{ domainLabel }}
            </span>
            <h1 class="mt-1 text-xl font-semibold text-secondary-900">
              {{ expiredHeading }}
            </h1>
            <!-- The duration used to be omitted because the screen could not
                 tell which token had expired. `?flow=` now says — see the
                 computed above, which mirrors the two server constants. -->
            <p class="mt-1 text-sm text-secondary-500">
              {{ expiredSubheading }}
            </p>
          </div>
          <RouterLink to="/forgot-password" class="block">
            <PButton label="Send me a new link" class="primary-btn w-full" />
          </RouterLink>
        </template>

        <!-- Reset form -->
        <template v-else>
          <div class="mb-6">
            <span
              v-if="isOps"
              class="text-xs font-semibold uppercase tracking-[0.08em] text-primary-700"
            >
              {{ domainLabel }}
            </span>
            <h1 class="mt-1 text-xl font-semibold text-secondary-900">
              {{ heading }}
            </h1>
            <!-- The account address stays visually distinct from the sentence
                 around it — it is the one thing on this screen the reader needs
                 to recognise, so it is not allowed to dissolve into body copy.
                 Hence the split rather than interpolating `subheading`. -->
            <p class="mt-1 text-sm text-secondary-500">
              <template v-if="accountEmail && isReset">
                This replaces the current password for
                <span class="font-medium text-secondary-700">{{ accountEmail }}</span>.
              </template>
              <template v-else-if="accountEmail">
                You'll use it to sign in as
                <span class="font-medium text-secondary-700">{{ accountEmail }}</span>.
              </template>
              <template v-else>
                {{ subheadingWithoutAccount }}
              </template>
            </p>
          </div>

          <form @submit.prevent="handleSubmit" class="space-y-4">
            <div>
              <label for="password" class="mb-1.5 block text-sm font-medium text-secondary-700">
                New password
              </label>
              <PPassword
                inputId="password"
                v-model="password"
                :feedback="false"
                toggleMask
                placeholder="Enter new password"
                class="light-input w-full"
                inputClass="w-full"
                :disabled="loading"
                :inputProps="{ autocomplete: 'new-password' }"
              />
              <!-- `inputProps`, not a bare `autocomplete`: PrimeVue 4.5's
                   Password sets inheritAttrs:false and puts plain attributes on
                   its wrapper div, so the old attribute never reached the
                   <input> and password managers offered nothing (P2). -->

              <!-- Live mirror of the server policy — see utils/passwordPolicy.ts -->
              <ul class="mt-3 space-y-1.5">
                <li
                  v-for="rule in policyRules"
                  :key="rule.key"
                  class="flex items-start gap-2 text-xs leading-5"
                  :class="rule.met ? 'text-success-700' : 'text-secondary-500'"
                >
                  <!-- A hollow ring reads as a radio button waiting to be
                       chosen; an unmet rule is a state, not a control. -->
                  <i
                    class="pi mt-1"
                    :class="rule.met
                      ? 'pi-check text-[10px] text-success-600'
                      : 'pi-circle-fill text-[5px] text-secondary-300'"
                    aria-hidden="true"
                  />
                  <span>{{ rule.label }}</span>
                </li>
              </ul>
            </div>

            <div>
              <label for="confirmPassword" class="mb-1.5 block text-sm font-medium text-secondary-700">
                Confirm password
              </label>
              <PPassword
                inputId="confirmPassword"
                v-model="confirmPassword"
                :feedback="false"
                toggleMask
                placeholder="Confirm new password"
                class="light-input w-full"
                inputClass="w-full"
                :disabled="loading"
                :inputProps="{ autocomplete: 'new-password' }"
              />
              <p
                v-if="confirmPassword && !passwordsMatch"
                class="mt-2 text-xs text-danger-600"
              >
                Both fields must match.
              </p>
            </div>

            <PButton
              type="submit"
              :label="submitLabel"
              :loading="loading"
              :disabled="!canSubmit"
              class="primary-btn w-full"
            />
          </form>
        </template>

        <!-- Footer — same anatomy as LoginView, so the two screens read as one place -->
        <div class="mt-8 text-center text-xs text-secondary-500">
          <p>
            &copy; {{ new Date().getFullYear() }} ClaimShield &middot; Safety &amp; Compliance Services
          </p>
          <p class="mt-1.5">
            <RouterLink
              to="/login"
              class="underline underline-offset-2 transition-colors hover:text-primary-700"
            >Back to sign in</RouterLink>
          </p>
        </div>
       </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Anatomy, mesh recipe, inputs and button are LoginView's, deliberately — the
   invitee sets a password here and signs in there, seconds apart. Values come
   from knowledge/brand/tokens.md; nothing here is invented. */

.auth-page {
  background: #fff;
  min-height: 100vh;
}

/* ── Left mesh panel — the Breathing Surface (playbook 3.2, frozen recipe) ── */
.mesh-panel {
  position: relative;
  overflow: hidden;
  border-radius: 1.5rem;
}

.mesh-bg {
  position: absolute;
  inset: 0;
  background: var(--cs-purple);
  background:
    radial-gradient(ellipse at 20% 50%, rgba(107, 79, 175, 0.9) 0%, transparent 60%),
    radial-gradient(ellipse at 70% 20%, rgba(139, 92, 246, 0.7) 0%, transparent 50%),
    radial-gradient(ellipse at 40% 80%, rgba(59, 130, 246, 0.5) 0%, transparent 50%),
    radial-gradient(ellipse at 80% 70%, rgba(168, 85, 247, 0.6) 0%, transparent 45%),
    radial-gradient(ellipse at 10% 20%, rgba(236, 72, 153, 0.3) 0%, transparent 50%),
    linear-gradient(135deg, var(--cs-purple-dark), var(--cs-purple-light));
  background-size: 200% 200%;
  animation: mesh-shift 6s ease-in-out infinite;
}

.mesh-bg::after {
  content: '';
  position: absolute;
  inset: 0;
  backdrop-filter: blur(40px);
  -webkit-backdrop-filter: blur(40px);
}

@keyframes mesh-shift {
  0%, 100% { background-position: 0% 50%; }
  25% { background-position: 100% 25%; }
  50% { background-position: 50% 100%; }
  75% { background-position: 25% 0%; }
}

/* The canon calls this guard mandatory for the Breathing Surface: a slow
   full-panel oscillation is exactly the motion class that triggers vestibular
   symptoms. The surface stays — only the 6s orbit stops. */
@media (prefers-reduced-motion: reduce) {
  .mesh-bg { animation: none; background-position: 0% 50%; }
}

/* ── Inputs ── */
input.light-input,
.light-input :deep(input) {
  background: #fff !important;
  border: 1px solid theme('colors.secondary.200') !important;
  border-radius: 0.5rem !important;
  padding: 0.5rem 0.75rem !important;
  font-size: 0.875rem !important;
  color: theme('colors.secondary.800') !important;
  transition: border-color 0.2s, box-shadow 0.2s !important;
}

input.light-input:focus,
.light-input :deep(input:focus) {
  border-color: var(--cs-purple-light) !important;
  box-shadow: 0 0 0 3px rgba(107, 79, 175, 0.16) !important;
  outline: none !important;
}

input.light-input::placeholder,
.light-input :deep(input::placeholder) {
  color: theme('colors.secondary.400') !important;
}

.light-input :deep(.p-password-toggle-mask-btn) {
  color: theme('colors.secondary.400');
}

.light-input :deep(.p-password-toggle-mask-btn:hover) {
  color: theme('colors.secondary.500');
}

/* ── Primary button ── */
.primary-btn {
  background: linear-gradient(135deg, var(--cs-purple-light), var(--cs-purple)) !important;
  border: none !important;
  color: #fff !important;
  border-radius: 0.5rem !important;
  padding: 0.6875rem 1.25rem !important;
  min-height: 44px !important;
  font-weight: 600 !important;
  font-size: 0.875rem !important;
  transition: all 0.2s !important;
}

.primary-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #7c63bf, #4a30a0) !important;
  box-shadow: 0 4px 12px rgba(107, 79, 175, 0.3) !important;
}

.primary-btn:active:not(:disabled) {
  background: linear-gradient(135deg, #5a3f9e, var(--cs-purple-dark)) !important;
}

/* A disabled CTA must read as unavailable, not as a slightly dim CTA — the
   checklist above it is what explains why. */
.primary-btn:disabled {
  background: theme('colors.secondary.200') !important;
  color: theme('colors.secondary.500') !important;
  box-shadow: none !important;
}
</style>
